import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { displayNotice, RootState } from "./../../globals";
import DashboardService from "../../services/v2/dashboard.service";
import BarChart, { BarDatum } from "./barChart";
import KpiCard from "./kpiCard";
import "./dashboard.css";
import { numeroConComas }
	from "./numero"

/*
	Tablero de inicio. Reemplaza la version que pedia los mismos datos cuatro
	veces (una por tarjeta) y prendia el candado global de "cargando" en cada
	llamada, lo que hacia parpadear toda la app al entrar. Ahora es una sola
	peticion al abrir y una parcial cuando cambias el rango de una grafica.
*/

const RANGOS: Record<string, { days: number; group: "days" | "weeks" }> = {
	"Últimos 7 días": { days: 7, group: "days" },
	"Últimos 15 días": { days: 15, group: "days" },
	"Últimos 30 días por semana": { days: 30, group: "weeks" },
};
const RANGO_INICIAL = "Últimos 7 días";

const MESES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

/* el backend entrega las fechas como dd-mm-yy */
const etiquetaFecha = (crudo: string): string => {
	const p = (crudo || "").split("-");
	if (p.length !== 3) return crudo || "";
	const dia = parseInt(p[0], 10);
	const mes = parseInt(p[1], 10);
	if (isNaN(dia) || isNaN(mes) || mes < 1 || mes > 12) return crudo;
	return `${dia} ${MESES[mes - 1]}`;
};

const aSerie = (filas: any): BarDatum[] =>
	Array.isArray(filas)
		? filas
			.filter((f: any) => f && typeof f.total === "number" && (f.day || f.week))
			.map((f: any) => ({ label: etiquetaFecha(f.day || f.week), total: f.total }))
		: [];

const Icono = ({ children }: { children: React.ReactNode }) => (
	<span className="wt-icon" aria-hidden="true">
		<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
			strokeLinecap="round" strokeLinejoin="round">
			{children}
		</svg>
	</span>
);

const Dashboard = () => {
	const dispatch = useDispatch();
	const userProfile: any = useSelector((state: RootState) => state.userProfile.value);
	const currentCampus: any = useSelector(
		(state: RootState) => state.rootReducer.sessionState.currentCampus
	);

	const [alumnosActivos, setAlumnosActivos] = useState<number | null>(null);
	const [horariosActivos, setHorariosActivos] = useState<number | null>(null);
	const [altas, setAltas] = useState<BarDatum[]>([]);
	const [retiros, setRetiros] = useState<BarDatum[]>([]);

	const [alertas, setAlertas] = useState<Record<string, number> | null>(null);
	const [rangoAltas, setRangoAltas] = useState<string>(RANGO_INICIAL);
	const [rangoRetiros, setRangoRetiros] = useState<string>(RANGO_INICIAL);

	const [cargandoTodo, setCargandoTodo] = useState<boolean>(true);
	const [cargandoAlertas, setCargandoAlertas] = useState<boolean>(true);
	const [cargandoAltas, setCargandoAltas] = useState<boolean>(false);
	const [cargandoRetiros, setCargandoRetiros] = useState<boolean>(false);

	/*
		"mentor" (Profesor) es el unico rol de aula; todos los demas son administrativos.
		Se mira la lista completa de roles y no el que traiga puesto en ese momento:
		quien da clase y ademas lleva un puesto administrativo ve el tablero completo
		aunque este navegando como profesor.
	*/
	const soloProfesor = useMemo(
		() => {
			const mapa = userProfile?.roleMap;
			if (!mapa) return true;
			return Object.keys(mapa).filter((codigo) => codigo !== "mentor").length === 0;
		},
		[userProfile]
	);

	const avisarError = useCallback(() => {
		dispatch(
			displayNotice({
				heading: <h3 style={{ color: "var(--wa-oro)", display: "inline-block" }}>Error</h3>,
				message: "No pudimos cargar la información del tablero.",
				cornerClose: false,
			})
		);
	}, [dispatch]);

	/*
		Dos peticiones en paralelo, no una.

		Los nueve indicadores de "Requieren atencion" y "Oportunidad comercial"
		cuestan solos unos 900ms, mientras que los de arriba y las graficas
		tardan 250. Pedirlo todo junto hacia esperar 760ms para ver el primer
		numero. Separado, la parte de arriba pinta en cuanto esta y los avisos
		entran despues sobre su propio esqueleto, que ya existia.
	*/
	useEffect(() => {
		let vigente = true;

		const init = async () => {
			if (currentCampus === null || soloProfesor) {
				setCargandoTodo(false);
				setCargandoAlertas(false);
				return;
			}

			setCargandoTodo(true);
			setCargandoAlertas(true);

			const servicio = new DashboardService();

			const arriba = servicio
				.getData({
					days: RANGOS[RANGO_INICIAL].days,
					group: RANGOS[RANGO_INICIAL].group,
					options: {
						include_new: true,
						include_retired: true,
						include_active_students: true,
						include_active_schedules: true,
						include_alerts: false,
					},
				})
				.then((r: any) => {
					if (!vigente) return;
					if (r?.status === 200 || r?.status === 204) {
						setAlumnosActivos(Number(r.data?.active_students) || 0);
						setHorariosActivos(Number(r.data?.active_schedules) || 0);
						setAltas(aSerie(r.data?.new_enrollments));
						setRetiros(aSerie(r.data?.retired_enrollments));
					} else {
						avisarError();
					}
				})
				.catch(() => { if (vigente) avisarError(); })
				.finally(() => { if (vigente) setCargandoTodo(false); });

			const avisos = servicio
				.getData({
					days: RANGOS[RANGO_INICIAL].days,
					group: RANGOS[RANGO_INICIAL].group,
					options: {
						include_new: false,
						include_retired: false,
						include_active_students: false,
						include_active_schedules: false,
						include_alerts: true,
					},
				})
				.then((r: any) => {
					if (!vigente) return;
					if (r?.status === 200 || r?.status === 204) {
						setAlertas({
							vencidas: Number(r.data?.overdue_tuition_students) || 0,
							sinHorarioActivo: Number(r.data?.students_without_active_schedule) || 0,
							sinNingunHorario: Number(r.data?.students_without_any_schedule) || 0,
							bajoMinimo: Number(r.data?.schedules_below_minimum) || 0,
							inactivosConActivos: Number(r.data?.inactive_schedules_with_active_students) || 0,
							ausenciaAlta: Number(r.data?.schedules_high_absence) || 0,
							sinKit: Number(r.data?.students_without_kit) || 0,
							moraArticulos: Number(r.data?.students_owing_articles) || 0,
							sinVitrina: Number(r.data?.students_without_store_purchases) || 0,
						});
					} else {
						avisarError();
					}
				})
				.catch(() => { if (vigente) avisarError(); })
				.finally(() => { if (vigente) setCargandoAlertas(false); });

			await Promise.all([arriba, avisos]);
		};

		init();
		return () => { vigente = false; };
	}, [currentCampus, soloProfesor, avisarError]);

	/* al cambiar un rango solo se vuelve a pedir esa serie */
	const recargarSerie = useCallback(
		async (cual: "altas" | "retiros", rango: string) => {
			const { days, group } = RANGOS[rango];
			const setCargando = cual === "altas" ? setCargandoAltas : setCargandoRetiros;
			setCargando(true);
			try {
				const servicio = new DashboardService();
				const r = await servicio.getData({
					days,
					group,
					options: {
						include_new: cual === "altas",
						include_retired: cual === "retiros",
						include_active_students: false,
						include_active_schedules: false,
					},
				});
				if (r?.status === 200 || r?.status === 204) {
					if (cual === "altas") setAltas(aSerie(r.data?.new_enrollments));
					else setRetiros(aSerie(r.data?.retired_enrollments));
				} else {
					avisarError();
				}
			} catch (e) {
				avisarError();
			} finally {
				setCargando(false);
			}
		},
		[avisarError]
	);

	const cambiarAltas = (r: string) => { setRangoAltas(r); recargarSerie("altas", r); };
	const cambiarRetiros = (r: string) => { setRangoRetiros(r); recargarSerie("retiros", r); };

	const rangos = useMemo(() => Object.keys(RANGOS), []);
	const nombre = userProfile?.first_name || "";

	const cifra = (v: number | null) =>
		cargandoTodo || v === null
			? <span className="wt-n wt-skeleton">000</span>
			: <span className="wt-n">{numeroConComas(v)}</span>;

	return (
		<div className="wapp-tablero">
			<div className="wt-hello">
	<svg className="wt-mark" viewBox="0 0 112 112" role="img" aria-label="WAPP">
		<rect width="112" height="112" rx="17.92" fill="var(--wt-orange)" />
		<path fill="#FFFFFF" transform="translate(17.31 91.64) scale(0.108 -0.108)"
			d="M110.00146484375 0 6.240234375 660H176.92041015625L224.8017578125 140.320556640625H229.961669921875L281.04345703125 660H461.9638671875L516.045654296875 140.320556640625H520.485595703125L567.806884765625 660H728.76708984375L622.845947265625 0H416.28515625L369.7236328125 519.0390625H366.7236328125L317.442138671875 0Z" />
	</svg>
				<div className="wt-greet">
					<h1>Hola <em>{nombre}</em></h1>
					<p>
						Plantel <b>{currentCampus ? currentCampus.name : "desconocido"}</b>
					</p>
				</div>
			</div>

			{soloProfesor && (
				<section className="wt-band">
					<div className="wt-pronto">
						<span className="wt-pronto-sello">Muy pronto</span>
						<p>
							Aquí vas a ver tus grupos, tus asistencias y tu calendario de clases.
							Lo estamos preparando.
						</p>
					</div>
				</section>
			)}

			{!soloProfesor && (
				<section className="wt-band">
					<header>
						<h2>Pulso del plantel</h2>
						<span className="wt-rule" />
					</header>
					<div className="wt-grid wt-pulse">

						<div className="wt-kpi wt-hero">
							<span className="wt-label">
								<Icono>
									<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
									<circle cx="9" cy="7" r="4" />
									<path d="m16 11 2 2 4-4" />
								</Icono>
								Alumnos activos
							</span>
							<span className="wt-figure">{cifra(alumnosActivos)}</span>
						</div>

						<div className="wt-kpi wt-hero">
							<span className="wt-label">
								<Icono>
									<circle cx="12" cy="12" r="9" />
									<path d="M12 7v5l3.2 2" />
								</Icono>
								Horarios activos
							</span>
							<span className="wt-figure">{cifra(horariosActivos)}</span>
						</div>

						<div className="wt-wide">
							<BarChart
								titulo="Nuevas matrículas"
								datos={altas}
								tono="normal"
								rotuloHoy={RANGOS[rangoAltas].group === "weeks" ? "Esta semana" : "Hoy"}
								rangos={rangos}
								rangoActivo={rangoAltas}
								onRango={cambiarAltas}
								cargando={cargandoTodo || cargandoAltas}
								idSelect="wt-rango-altas"
							/>
						</div>

					</div>
				</section>
			)}

			{!soloProfesor && (
				<section className="wt-band">
					<header>
						<h2>Requieren atención</h2>
						<span className="wt-rule" />
					</header>
					<div className="wt-grid wt-alert">
						<KpiCard tipo="alerta" cargando={cargandoAlertas} etiqueta="Alumnos con mensualidades vencidas"
							valor={alertas?.vencidas ?? null} de={alumnosActivos}
							icono={<><rect x="2" y="6" width="20" height="13" rx="2" /><circle cx="9" cy="12.5" r="2.5" /><path d="m16 10 5 5m0-5-5 5" /></>} />
						<KpiCard tipo="alerta" cargando={cargandoAlertas} etiqueta="Alumnos sin horario activo" matiz="(retirados)"
							valor={alertas?.sinHorarioActivo ?? null}
							icono={<><path d="M4 19V6a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v13" /><path d="M4 19h16" /><path d="m8 9 8 6m0-6-8 6" /></>} />
						<KpiCard tipo="alerta" cargando={cargandoAlertas} etiqueta="Alumnos sin ningún horario"
							valor={alertas?.sinNingunHorario ?? null}
							icono={<><circle cx="12" cy="7" r="4" /><path d="M5.5 21a6.5 6.5 0 0 1 13 0" /><path d="M19 3v6" /></>} />
						<KpiCard tipo="alerta" cargando={cargandoAlertas} etiqueta="Horarios debajo del mínimo de alumnos"
							valor={alertas?.bajoMinimo ?? null} de={horariosActivos}
							icono={<><path d="M3.5 17a9 9 0 1 1 17 0" /><path d="m12 13 4-3.5" /></>} />
						<KpiCard tipo="alerta" cargando={cargandoAlertas} etiqueta="Horarios inactivos con alumnos activos"
							valor={alertas?.inactivosConActivos ?? null}
							icono={<><circle cx="12" cy="12" r="9" /><path d="m9 9 6 6m0-6-6 6" /></>} />
						<KpiCard tipo="alerta" cargando={cargandoAlertas} etiqueta="Horarios con ausencia alta" matiz="(+15%)"
							valor={alertas?.ausenciaAlta ?? null} de={horariosActivos}
							icono={<><path d="M3 20h18" /><path d="M6 20v-5h12v5" /><path d="M9 15V9h6v6" /><path d="M12 3v3" /></>} />
					</div>
				</section>
			)}

			{!soloProfesor && (
				<section className="wt-band">
					<header>
						<h2>Oportunidad comercial</h2>
						<span className="wt-rule" />
					</header>
					<div className="wt-grid wt-sales">
						<KpiCard tipo="venta" cargando={cargandoAlertas} etiqueta="Alumnos sin kit de módulo"
							valor={alertas?.sinKit ?? null} de={alumnosActivos}
							icono={<><path d="M3 8.5 12 4l9 4.5v7L12 20l-9-4.5z" /><path d="M3 8.5 12 13l9-4.5" /><path d="M12 13v7" /></>} />
						<KpiCard tipo="venta" cargando={cargandoAlertas} etiqueta="Alumnos con mora en kits y vitrina"
							valor={alertas?.moraArticulos ?? null} de={alumnosActivos}
							icono={<><ellipse cx="12" cy="6" rx="8" ry="3" /><path d="M4 6v6c0 1.7 3.6 3 8 3s8-1.3 8-3V6" /><path d="M4 12v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" /></>} />
						<KpiCard tipo="venta" cargando={cargandoAlertas} etiqueta="Alumnos sin compras de vitrina"
							valor={alertas?.sinVitrina ?? null} de={alumnosActivos}
							icono={<><path d="M20.5 12.5 12.6 20.4a2 2 0 0 1-2.8 0l-6.2-6.2a2 2 0 0 1 0-2.8l7.9-7.9 6.3.6.7 6.3z" /><circle cx="15.5" cy="8.5" r="1.3" /></>} />
					</div>
				</section>
			)}

			{!soloProfesor && (
				<section className="wt-band">
					<header>
						<h2>Retiros</h2>
						<span className="wt-rule" />
					</header>
					<BarChart
						titulo="Resumen de retirados"
						datos={retiros}
						tono="urgente"
						rotuloHoy={RANGOS[rangoRetiros].group === "weeks" ? "Esta semana" : "Hoy"}
						rangos={rangos}
						rangoActivo={rangoRetiros}
						onRango={cambiarRetiros}
						cargando={cargandoTodo || cargandoRetiros}
						idSelect="wt-rango-retiros"
					/>
				</section>
			)}
		</div>
	);
};

export default Dashboard;
